import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import SEO from '../components/SEO';
import { 
  FileText, Upload, Check, AlertCircle, Car, Shield, 
  CreditCard, RefreshCw, CheckCircle, Calendar, Lock, ExternalLink, Mail
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Navbar } from '../components/Navbar';

function DriverDocumentUploadPage() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const applicationId = searchParams.get('id');
  const resumeToken = searchParams.get('resume');
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [applicationData, setApplicationData] = useState(null);
  const [error, setError] = useState(null);
  const [requestingResumeLink, setRequestingResumeLink] = useState(false);
  
  const [documents, setDocuments] = useState({
    license: { document_url: '', document_number: '', expiry_date: '' },
    insurance: { document_url: '', policy_number: '', expiry_date: '' },
    registration: { document_url: '', registration_number: '' },
    right_to_work: { document_url: '' }
  });
  
  const [uploadingDoc, setUploadingDoc] = useState(null);

  useEffect(() => {
    const appId = applicationId || resumeToken;
    if (appId) {
      fetchApplicationData(appId);
    } else {
      setError('No application ID provided');
      setLoading(false);
    }
  }, [applicationId, resumeToken]);

  const fetchApplicationData = async (appId) => {
    try {
      const response = await api.get(`/applications/driver/${appId}/documents`);
      setApplicationData(response.data);
      
      if (!response.data.can_upload) {
        setError(`Documents cannot be uploaded. Application status: ${response.data.status}`);
      }
    } catch (err) {
      setError('Failed to load application. Please check the link and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestResumeLink = async () => {
    const appId = applicationId || resumeToken;
    if (!appId) return;
    
    setRequestingResumeLink(true);
    try {
      await api.post(`/applications/driver/${appId}/request-resume-link`);
      toast.success('A new link has been sent to your email!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send resume link');
    } finally {
      setRequestingResumeLink(false);
    }
  };

  const handleFileUpload = async (docType, file) => {
    if (!file) return;
    
    const appId = applicationId || resumeToken;
    setUploadingDoc(docType);
    
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload a PDF, JPEG, PNG, or WebP file.');
      setUploadingDoc(null);
      return;
    }
    
    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 10MB.');
      setUploadingDoc(null);
      return;
    }
    
    // Create FormData for file upload
    const formData = new FormData();
    formData.append('file', file);
    formData.append('application_id', appId);
    formData.append('document_type', docType);
    
    try {
      // Upload to driver document endpoint
      const response = await api.post('/upload/driver-document', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setDocuments(prev => ({
        ...prev,
        [docType]: { ...prev[docType], document_url: response.data.url }
      }));
      
      toast.success(`${docType.replace('_', ' ')} uploaded successfully`);
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to upload document';
      toast.error(errorMsg);
    } finally {
      setUploadingDoc(null);
    }
  };

  const handleInputChange = (docType, field, value) => {
    setDocuments(prev => ({
      ...prev,
      [docType]: { ...prev[docType], [field]: value }
    }));
  };

  const canSubmit = () => {
    const vehicleType = applicationData?.vehicle_type || 'bike';
    
    // Required for all: license, insurance
    if (!documents.license.document_url || !documents.license.document_number || !documents.license.expiry_date) {
      return false;
    }
    if (!documents.insurance.document_url || !documents.insurance.policy_number || !documents.insurance.expiry_date) {
      return false;
    }
    
    // Registration required for car/scooter
    if (['car', 'scooter'].includes(vehicleType)) {
      if (!documents.registration.document_url) {
        return false;
      }
    }
    
    return true;
  };

  const handleSubmit = async () => {
    if (!canSubmit()) {
      toast.error('Please upload all required documents and fill in the details');
      return;
    }
    
    setSubmitting(true);
    try {
      await api.post(`/applications/driver/${applicationId}/documents`, documents);
      toast.success('Documents submitted successfully! We will review them shortly.');
      
      // Show success state
      setApplicationData(prev => ({ ...prev, status: 'pending_review', can_upload: false }));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit documents');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div>
        <Navbar />
        <div className="min-h-screen bg-gradient-to-b from-orange-50 to-white pt-20 flex items-center justify-center">
          <div className="text-center">
            <RefreshCw className="w-12 h-12 mx-auto mb-4 text-orange-500 animate-spin" />
            <p className="text-slate-600">Loading application...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !applicationData?.can_upload) {
    return (
      <div>
        <Navbar />
        <div className="min-h-screen bg-gradient-to-b from-orange-50 to-white pt-20 pb-12">
          <div className="max-w-xl mx-auto px-4">
            <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
              {applicationData?.status === 'pending_review' ? (
                <>
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-blue-100 flex items-center justify-center">
                    <RefreshCw className="w-8 h-8 text-blue-500" />
                  </div>
                  <h2 className="text-xl font-semibold text-slate-800 mb-2">Documents Under Review</h2>
                  <p className="text-slate-500">Your documents have been submitted and are being reviewed. You'll receive an email once approved.</p>
                </>
              ) : applicationData?.status === 'approved' ? (
                <>
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
                    <CheckCircle className="w-8 h-8 text-green-500" />
                  </div>
                  <h2 className="text-xl font-semibold text-slate-800 mb-2">Application Approved!</h2>
                  <p className="text-slate-500">Congratulations! Your application has been approved. Check your email for your driver login code.</p>
                </>
              ) : applicationData?.status === 'rejected' ? (
                <>
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
                    <AlertCircle className="w-8 h-8 text-red-500" />
                  </div>
                  <h2 className="text-xl font-semibold text-slate-800 mb-2">Documents Rejected</h2>
                  <p className="text-slate-500 mb-4">
                    {applicationData?.rejection_reason || 'Your documents did not meet our requirements. Please request a new upload link to try again.'}
                  </p>
                  <Button 
                    onClick={handleRequestResumeLink} 
                    disabled={requestingResumeLink}
                    className="btn-primary"
                    data-testid="request-new-link-btn"
                  >
                    {requestingResumeLink ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Mail className="w-4 h-4 mr-2" />
                        Request New Upload Link
                      </>
                    )}
                  </Button>
                </>
              ) : (
                <>
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
                    <AlertCircle className="w-8 h-8 text-red-500" />
                  </div>
                  <h2 className="text-xl font-semibold text-slate-800 mb-2">Unable to Upload Documents</h2>
                  <p className="text-slate-500">{error || 'This application is not in the correct state for document upload.'}</p>
                </>
              )}
              <Button onClick={() => navigate('/')} className="mt-6" variant="outline">
                Return Home
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const vehicleType = applicationData?.vehicle_type || 'bike';
  const needsRegistration = ['car', 'scooter'].includes(vehicleType);

  return (
    <>
      <SEO 
        title="Upload Documents - Driver Application"
        description="Upload your documents to complete your Hambre driver application"
      />
      <div>
        <Navbar />
        <div className="min-h-screen bg-gradient-to-b from-orange-50 to-white pt-20 pb-12">
          <div className="max-w-2xl mx-auto px-4">
            {/* Header */}
            <div className="text-center mb-8">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-orange-100 flex items-center justify-center">
                <FileText className="w-8 h-8 text-orange-500" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900">Upload Your Documents</h1>
              <p className="text-slate-500 mt-2">
                Hi {applicationData?.full_name}, please upload the required documents to complete your application.
              </p>
            </div>

            {/* GDPR Privacy Notice */}
            <div className="bg-green-50 border border-green-200 rounded-xl p-5 mb-6" data-testid="gdpr-notice">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                  <Lock className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-green-800 mb-2">Your Documents Are Safe</h3>
                  <ul className="space-y-1.5 text-sm text-green-700">
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <span>Documents are <strong>deleted immediately</strong> after verification - we do not keep copies</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <span>Only essential metadata (license number, expiry dates) is stored after approval</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <span>Your data is protected in compliance with GDPR regulations</span>
                    </li>
                  </ul>
                  <Link 
                    to="/driver-document-policy" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 mt-3 text-sm font-medium text-green-700 hover:text-green-800 underline"
                    data-testid="privacy-policy-link"
                  >
                    Read our full Document Handling Policy
                    <ExternalLink className="w-3.5 h-3.5" />
                  </Link>
                </div>
              </div>
            </div>

            {/* Document Upload Cards */}
            <div className="space-y-6">
              {/* Driver's License */}
              <div className="bg-white rounded-xl shadow-sm p-6 border border-slate-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                    <CreditCard className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">Driver's License</h3>
                    <p className="text-sm text-slate-500">Valid driving license (front & back)</p>
                  </div>
                  <span className="ml-auto text-xs bg-red-100 text-red-600 px-2 py-1 rounded">Required</span>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <Label>Upload Document</Label>
                    <div className="mt-1 flex items-center gap-3">
                      <input
                        type="file"
                        accept="image/*,.pdf"
                        onChange={(e) => handleFileUpload('license', e.target.files[0])}
                        className="hidden"
                        id="license-upload"
                        data-testid="license-upload"
                      />
                      <label
                        htmlFor="license-upload"
                        className={`flex-1 border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors ${
                          documents.license.document_url 
                            ? 'border-green-300 bg-green-50' 
                            : 'border-slate-300 hover:border-orange-300'
                        }`}
                      >
                        {uploadingDoc === 'license' ? (
                          <RefreshCw className="w-6 h-6 mx-auto text-orange-500 animate-spin" />
                        ) : documents.license.document_url ? (
                          <div className="flex items-center justify-center gap-2 text-green-600">
                            <Check className="w-5 h-5" />
                            <span>Document uploaded</span>
                          </div>
                        ) : (
                          <div className="flex items-center justify-center gap-2 text-slate-500">
                            <Upload className="w-5 h-5" />
                            <span>Click to upload</span>
                          </div>
                        )}
                      </label>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="license-number">License Number</Label>
                      <Input
                        id="license-number"
                        placeholder="e.g., SMITH123456AB9CD"
                        value={documents.license.document_number}
                        onChange={(e) => handleInputChange('license', 'document_number', e.target.value)}
                        data-testid="license-number-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="license-expiry">Expiry Date</Label>
                      <Input
                        id="license-expiry"
                        type="date"
                        value={documents.license.expiry_date}
                        onChange={(e) => handleInputChange('license', 'expiry_date', e.target.value)}
                        min={new Date().toISOString().split('T')[0]}
                        data-testid="license-expiry-input"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Insurance */}
              <div className="bg-white rounded-xl shadow-sm p-6 border border-slate-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                    <Shield className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">Insurance Certificate</h3>
                    <p className="text-sm text-slate-500">Valid vehicle/delivery insurance</p>
                  </div>
                  <span className="ml-auto text-xs bg-red-100 text-red-600 px-2 py-1 rounded">Required</span>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <Label>Upload Document</Label>
                    <div className="mt-1">
                      <input
                        type="file"
                        accept="image/*,.pdf"
                        onChange={(e) => handleFileUpload('insurance', e.target.files[0])}
                        className="hidden"
                        id="insurance-upload"
                        data-testid="insurance-upload"
                      />
                      <label
                        htmlFor="insurance-upload"
                        className={`flex-1 border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors block ${
                          documents.insurance.document_url 
                            ? 'border-green-300 bg-green-50' 
                            : 'border-slate-300 hover:border-orange-300'
                        }`}
                      >
                        {uploadingDoc === 'insurance' ? (
                          <RefreshCw className="w-6 h-6 mx-auto text-orange-500 animate-spin" />
                        ) : documents.insurance.document_url ? (
                          <div className="flex items-center justify-center gap-2 text-green-600">
                            <Check className="w-5 h-5" />
                            <span>Document uploaded</span>
                          </div>
                        ) : (
                          <div className="flex items-center justify-center gap-2 text-slate-500">
                            <Upload className="w-5 h-5" />
                            <span>Click to upload</span>
                          </div>
                        )}
                      </label>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="insurance-number">Policy Number</Label>
                      <Input
                        id="insurance-number"
                        placeholder="e.g., POL-123456789"
                        value={documents.insurance.policy_number}
                        onChange={(e) => handleInputChange('insurance', 'policy_number', e.target.value)}
                        data-testid="insurance-number-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="insurance-expiry">Expiry Date</Label>
                      <Input
                        id="insurance-expiry"
                        type="date"
                        value={documents.insurance.expiry_date}
                        onChange={(e) => handleInputChange('insurance', 'expiry_date', e.target.value)}
                        min={new Date().toISOString().split('T')[0]}
                        data-testid="insurance-expiry-input"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Vehicle Registration - Only for car/scooter */}
              {needsRegistration && (
                <div className="bg-white rounded-xl shadow-sm p-6 border border-slate-200">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                      <Car className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900">Vehicle Registration</h3>
                      <p className="text-sm text-slate-500">V5C or equivalent registration document</p>
                    </div>
                    <span className="ml-auto text-xs bg-red-100 text-red-600 px-2 py-1 rounded">Required</span>
                  </div>
                  
                  <div className="space-y-4">
                    <div>
                      <Label>Upload Document</Label>
                      <div className="mt-1">
                        <input
                          type="file"
                          accept="image/*,.pdf"
                          onChange={(e) => handleFileUpload('registration', e.target.files[0])}
                          className="hidden"
                          id="registration-upload"
                          data-testid="registration-upload"
                        />
                        <label
                          htmlFor="registration-upload"
                          className={`flex-1 border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors block ${
                            documents.registration.document_url 
                              ? 'border-green-300 bg-green-50' 
                              : 'border-slate-300 hover:border-orange-300'
                          }`}
                        >
                          {uploadingDoc === 'registration' ? (
                            <RefreshCw className="w-6 h-6 mx-auto text-orange-500 animate-spin" />
                          ) : documents.registration.document_url ? (
                            <div className="flex items-center justify-center gap-2 text-green-600">
                              <Check className="w-5 h-5" />
                              <span>Document uploaded</span>
                            </div>
                          ) : (
                            <div className="flex items-center justify-center gap-2 text-slate-500">
                              <Upload className="w-5 h-5" />
                              <span>Click to upload</span>
                            </div>
                          )}
                        </label>
                      </div>
                    </div>
                    
                    <div>
                      <Label htmlFor="registration-number">Registration Number</Label>
                      <Input
                        id="registration-number"
                        placeholder="e.g., AB12 CDE"
                        value={documents.registration.registration_number}
                        onChange={(e) => handleInputChange('registration', 'registration_number', e.target.value)}
                        data-testid="registration-number-input"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Right to Work - Optional */}
              <div className="bg-white rounded-xl shadow-sm p-6 border border-slate-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
                    <FileText className="w-5 h-5 text-slate-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">Right to Work</h3>
                    <p className="text-sm text-slate-500">Passport, visa, or share code</p>
                  </div>
                  <span className="ml-auto text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded">Optional</span>
                </div>
                
                <div>
                  <Label>Upload Document</Label>
                  <div className="mt-1">
                    <input
                      type="file"
                      accept="image/*,.pdf"
                      onChange={(e) => handleFileUpload('right_to_work', e.target.files[0])}
                      className="hidden"
                      id="rtw-upload"
                      data-testid="rtw-upload"
                    />
                    <label
                      htmlFor="rtw-upload"
                      className={`flex-1 border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors block ${
                        documents.right_to_work.document_url 
                          ? 'border-green-300 bg-green-50' 
                          : 'border-slate-300 hover:border-orange-300'
                      }`}
                    >
                      {uploadingDoc === 'right_to_work' ? (
                        <RefreshCw className="w-6 h-6 mx-auto text-orange-500 animate-spin" />
                      ) : documents.right_to_work.document_url ? (
                        <div className="flex items-center justify-center gap-2 text-green-600">
                          <Check className="w-5 h-5" />
                          <span>Document uploaded</span>
                        </div>
                      ) : (
                        <div className="flex items-center justify-center gap-2 text-slate-500">
                          <Upload className="w-5 h-5" />
                          <span>Click to upload (optional)</span>
                        </div>
                      )}
                    </label>
                  </div>
                </div>
              </div>

              {/* Info Box */}
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                <div className="flex gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-amber-800">
                    <p className="font-medium mb-1">Important Information</p>
                    <ul className="list-disc list-inside space-y-1 text-amber-700">
                      <li>Documents must be clear and readable</li>
                      <li>All information must match your application</li>
                      <li>Expired documents will not be accepted</li>
                      <li>We'll notify you when documents need renewal</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <Button
                onClick={handleSubmit}
                disabled={!canSubmit() || submitting}
                className="w-full btn-primary py-6 text-lg"
                data-testid="submit-documents-btn"
              >
                {submitting ? (
                  <>
                    <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Check className="w-5 h-5 mr-2" />
                    Submit Documents
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default DriverDocumentUploadPage;
